import SEO from "../components/SEO";
import { ArrowRight, BarChart3, Users, Building2, Globe, Megaphone, Home, LineChart, CheckCircle, Menu, ChevronDown, Star, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "How It Works", action: () => navigate("/how-it-works") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  const steps = [
    {
      number: "1",
      icon: BarChart3,
      title: "Employee Retention Analysis",
      description: "CityWorth begins by analyzing workforce data to understand how homeownership may be influencing retention within your organization.",
      details: "Using employee address data and workforce tenure data, our proprietary system estimates:",
      items: [
        "Which employees are homeowners",
        "Which employees rent",
        "The median tenure of each group",
        "The potential retention impact of increasing employee homeownership"
      ],
      extra: "We also distribute a short employee survey to understand employee interest in homeownership and identify potential participation in the program."
    },
    {
      number: "2",
      icon: Users,
      title: "Review the Retention Analysis",
      description: "Once the analysis is complete, we schedule a meeting with your HR and leadership team to review the findings.",
      details: "During this session we discuss:",
      items: [
        "The relationship between homeownership and retention within your workforce",
        "Employee interest in homeownership",
        "The potential ROI of implementing a homeownership program"
      ],
      extra: "This conversation helps leadership understand how the program could impact retention and workforce stability."
    },
    {
      number: "3",
      icon: Building2,
      title: "Select the Right Retention Program",
      description: "Based on the analysis and company goals, organizations choose the retention approach that works best for their workforce.",
      customLayout: true
    },
    {
      number: "4",
      icon: Globe,
      title: "Launch Your Company Homeownership Portal",
      description: "CityWorth designs a company-branded homeownership portal where employees can learn about the benefit and begin their homeownership journey.",
      details: "The portal includes:",
      items: [
        "Educational resources",
        "Program details",
        "Homebuying rewards",
        "Direct access to CityWorth homebuying specialists"
      ],
      extra: "This creates a simple and centralized experience for employees."
    },
    {
      number: "5",
      icon: Megaphone,
      title: "Announce the Benefit to Employees",
      description: "CityWorth helps companies introduce the program to employees through a multi-channel rollout.",
      details: "This includes:",
      items: [
        "Employee announcement emails",
        "Text message notifications",
        "Company benefit communication materials"
      ],
      extra: "CityWorth also sends direct mail pieces to employees' homes so the entire household is aware of the new homeownership benefit. This approach increases awareness and encourages participation."
    },
    {
      number: "6",
      icon: Home,
      title: "Employees Begin Their Homeownership Journey",
      description: "Employees who are interested in the program can connect with the CityWorth team to begin preparing for homeownership.",
      details: "This may include:",
      items: [
        "Financial preparation",
        "Mortgage qualification",
        "Home search support",
        "Guidance through the homebuying process"
      ],
      extra: "Our team works directly with employees from preparation through closing.",
      note: "For companies offering Premium or Executive programs, down payment assistance may be applied toward the purchase."
    },
    {
      number: "7",
      icon: LineChart,
      title: "Ongoing Tracking and Reporting",
      description: "CityWorth provides ongoing reporting so companies can track the impact of the program.",
      details: "Metrics may include:",
      items: [
        "Number of employees exploring the program",
        "Employees actively working with the CityWorth team",
        "Employees purchasing homes",
        "Retention trends among participating employees"
      ],
      extra: "This data helps HR leaders and executives measure program performance and understand the impact on employee retention."
    }
  ];

  const benefits = [
    "A simple employee benefit",
    "Measurable retention insights",
    "Data that helps leadership evaluate program impact"
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="how-it-works-page">
      <SEO title="How It Works" description="Learn how CityWorth's homeownership employee benefit works — from onboarding to home purchase. A step-by-step guide for HR leaders." />
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" />
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/get-started">
              <Button className="btn-primary text-white px-6 py-2.5">Get Started</Button>
            </Link>
            
            <div className="relative">
              <Button 
                variant="outline" 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 border-slate-300 px-4 py-2.5"
              >
                <Menu className="w-5 h-5" />
                <span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {isMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    {menuItems.map((item, i) => (
                      <button
                        key={i}
                        onClick={item.action}
                        className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-12 pb-16 bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              How It{" "}
              <span className="gradient-text">Works</span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-6">
              CityWorth makes it simple for companies to implement a homeownership retention program. Our team handles the analysis, setup, and rollout so HR leaders can focus on supporting their workforce.
            </p>

            <p className="text-lg text-slate-500">
              The process is designed to be clear, data-driven, and easy to implement.
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={i} className="mb-16 last:mb-0">
                <div className="flex items-start gap-6">
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-blue-500 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <step.icon className="w-6 h-6 text-slate-400" />
                      <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{step.title}</h2>
                    </div>
                    
                    <p className="text-lg text-slate-600 mb-4">
                      {step.description}
                    </p>

                    {/* Custom layout for Step 3 - Programs */}
                    {step.customLayout && (
                      <div className="space-y-6">
                        {/* All Programs Grid */}
                        <div className="grid gap-4">
                          {/* Base Program - Full Width */}
                          <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-6 border-2 border-blue-300 relative overflow-hidden">
                            <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                              FOUNDATION
                            </div>
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                                <Users className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h4 className="font-bold text-blue-800 text-xl mb-2">Base Retention</h4>
                                <p className="text-slate-600 text-sm mb-3">
                                  The foundation program that introduces homeownership support and gives employees the tools and guidance needed to prepare for purchasing a home.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {["Homebuyer education", "Homebuying rewards", "Credit guidance", "Branded portal"].map((item, j) => (
                                    <span key={j} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>

                          <p className="text-slate-600 text-center font-medium py-2">
                            Add additional programs for enhanced retention:
                          </p>

                          {/* Premium & Executive Grid */}
                          <div className="grid md:grid-cols-2 gap-4">
                            {/* Premium */}
                            <div className="bg-white rounded-2xl p-6 border-2 border-green-400 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                              <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                                COMPANY-WIDE
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                                  <Star className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <h4 className="font-bold text-green-700 text-xl mb-2">Premium Retention</h4>
                                  <p className="text-slate-600 text-sm mb-3">
                                    Down payment assistance available for all full time employees who qualify.
                                  </p>
                                  <div className="text-green-600 font-semibold text-sm">
                                    $5,000 – $15,000 typical
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Executive */}
                            <div className="bg-white rounded-2xl p-6 border-2 border-purple-400 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                              <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                                STRATEGIC
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center flex-shrink-0">
                                  <Crown className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <h4 className="font-bold text-purple-700 text-xl mb-2">Executive Retention</h4>
                                  <p className="text-slate-600 text-sm mb-3">
                                    Targeted down payment assistance used to retain key employees and leadership.
                                  </p>
                                  <div className="text-purple-600 font-semibold text-sm">
                                    $15,000 – $50,000 typical
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {step.details && !step.customLayout && (
                      <p className="text-slate-700 font-medium mb-3">
                        {step.details}
                      </p>
                    )}

                    {step.items && (
                      <ul className="space-y-2 mb-4">
                        {step.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {step.baseProgram && (
                      <div className="bg-white rounded-xl p-5 border-2 border-blue-400 mb-4">
                        <h4 className="font-bold text-blue-700 mb-2 text-lg">{step.baseProgram.name}</h4>
                        <p className="text-slate-600 text-sm">{step.baseProgram.desc}</p>
                      </div>
                    )}

                    {step.programsIntro && (
                      <p className="text-slate-700 font-medium mb-3">
                        {step.programsIntro}
                      </p>
                    )}

                    {step.programs && (
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        {step.programs.map((program, j) => (
                          <div 
                            key={j} 
                            className={`rounded-xl p-5 border-2 bg-white ${
                              program.color === 'purple' 
                                ? 'border-purple-500' 
                                : program.color === 'gold'
                                ? 'border-amber-500'
                                : 'border-slate-200'
                            }`}
                          >
                            <h4 className={`font-bold mb-1 text-lg ${
                              program.color === 'purple' 
                                ? 'text-purple-700' 
                                : program.color === 'gold'
                                ? 'text-amber-700'
                                : 'text-slate-900'
                            }`}>{program.name}</h4>
                            <p className="text-slate-600 text-sm">{program.desc}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {step.extra && (
                      <p className="text-slate-500 italic">
                        {step.extra}
                      </p>
                    )}

                    {step.note && (
                      <p className="text-slate-600 mt-4 bg-slate-50 rounded-lg p-4 border border-slate-200">
                        For companies offering <span className="font-semibold text-green-600">Premium</span> or <span className="font-semibold text-purple-600">Executive</span> programs, down payment assistance may be applied toward the purchase.
                      </p>
                    )}
                  </div>
                </div>

                {/* Connector Line */}
                {i < steps.length - 1 && (
                  <div className="ml-8 mt-6 mb-6 border-l-2 border-dashed border-slate-200 h-8" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Simple Program Section */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              A Simple Program for{" "}
              <span className="gradient-text">HR Teams</span>
            </h2>
            
            <p className="text-lg text-slate-600 mb-8">
              CityWorth manages the setup, launch, and employee support so the program is easy for HR teams to implement.
            </p>

            <p className="text-slate-700 font-medium mb-6">Our goal is to provide:</p>

            <div className="flex flex-col items-center gap-4 mb-10">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3 border border-slate-200 shadow-sm w-full max-w-md">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 font-medium text-left">{benefit}</span>
                </div>
              ))}
            </div>

            <p className="text-lg text-slate-600 mb-10">
              With CityWorth, companies gain a partner focused on helping employees achieve homeownership while strengthening long-term workforce stability.
            </p>

            <Link to="/get-started">
              <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg">
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-8" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/how-it-works" className="text-slate-600 hover:text-slate-900">How It Works</Link>
            <Link to="/retention-programs" className="text-slate-600 hover:text-slate-900">Programs</Link>
            <Link to="/retention-analysis" className="text-slate-600 hover:text-slate-900">Analysis</Link>
            <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
          </div>
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;
